import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

type DialogVariant = "primary" | "danger" | "warning" | "success";

type DialogOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: DialogVariant;
};

type PromptOptions = DialogOptions & {
  defaultValue?: string;
  placeholder?: string;
  /** Use "password" for sensitive confirmations (e.g. disable MFA). */
  inputType?: "text" | "password";
  autoComplete?: string;
};

type ActiveDialog =
  | ({ kind: "alert" | "confirm" } & DialogOptions)
  | ({ kind: "prompt" } & PromptOptions);

type AppDialogApi = {
  alert: (options: DialogOptions | string) => Promise<void>;
  confirm: (options: DialogOptions | string) => Promise<boolean>;
  prompt: (options: PromptOptions | string) => Promise<string | null>;
};

const fallbackDialogApi: AppDialogApi = {
  alert: async () => undefined,
  confirm: async () => false,
  prompt: async () => null,
};

const AppDialogContext = createContext<AppDialogApi>(fallbackDialogApi);

const normalizeOptions = <T extends DialogOptions>(
  options: T | string,
): T =>
  (typeof options === "string" ? { message: options } : options) as T;

export const AppDialogProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { t } = useTranslation("common");
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const [promptValue, setPromptValue] = useState("");
  const resolverRef = useRef<((value: unknown) => void) | null>(null);

  const openDialog = useCallback(
    <T,>(dialog: ActiveDialog): Promise<T> =>
      new Promise<T>((resolve) => {
        resolverRef.current = resolve as (value: unknown) => void;
        setPromptValue(dialog.kind === "prompt" ? dialog.defaultValue ?? "" : "");
        setActiveDialog(dialog);
      }),
    [],
  );

  const alert = useCallback(
    async (options: DialogOptions | string) => {
      await openDialog<void>({ kind: "alert", ...normalizeOptions(options) });
    },
    [openDialog],
  );

  const confirm = useCallback(
    (options: DialogOptions | string) =>
      openDialog<boolean>({ kind: "confirm", ...normalizeOptions(options) }),
    [openDialog],
  );

  const prompt = useCallback(
    (options: PromptOptions | string) =>
      openDialog<string | null>({
        kind: "prompt",
        ...normalizeOptions<PromptOptions>(options),
      }),
    [openDialog],
  );

  const resolveDialog = useCallback((value: unknown) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setActiveDialog(null);
    resolver?.(value);
  }, []);

  const api = useMemo(() => ({ alert, confirm, prompt }), [alert, confirm, prompt]);
  const title =
    activeDialog?.title ??
    (activeDialog?.kind === "alert" ? t("notice") : t("confirm"));

  return (
    <AppDialogContext.Provider value={api}>
      {children}
      <Modal
        show={activeDialog !== null}
        onHide={() =>
          resolveDialog(activeDialog?.kind === "confirm" ? false : null)
        }
        centered
        aria-labelledby="app-dialog-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="app-dialog-title">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={activeDialog?.kind === "prompt" ? "mb-3" : "mb-0"}>
            {activeDialog?.message}
          </p>
          {activeDialog?.kind === "prompt" && (
            <Form.Control
              autoFocus
              type={activeDialog.inputType || "text"}
              autoComplete={
                activeDialog.autoComplete ||
                (activeDialog.inputType === "password"
                  ? "current-password"
                  : "off")
              }
              value={promptValue}
              placeholder={activeDialog.placeholder}
              onChange={(event) => setPromptValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") resolveDialog(promptValue);
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          {activeDialog?.kind !== "alert" && (
            <Button variant="secondary" onClick={() => resolveDialog(activeDialog?.kind === "confirm" ? false : null)}>
              {activeDialog?.cancelText ?? t("cancel")}
            </Button>
          )}
          <Button
            variant={activeDialog?.variant ?? "primary"}
            onClick={() =>
              resolveDialog(
                activeDialog?.kind === "prompt"
                  ? promptValue
                  : activeDialog?.kind === "confirm",
              )
            }
          >
            {activeDialog?.confirmText ??
              (activeDialog?.kind === "alert" ? t("close") : t("confirm"))}
          </Button>
        </Modal.Footer>
      </Modal>
    </AppDialogContext.Provider>
  );
};

export function useAppDialog(): AppDialogApi {
  return useContext(AppDialogContext);
}
